import { useMemo } from "react";
import { useColorMode } from "./ColorModeContext";
import { Mode } from "./appTheme";

/* ═══════════════════════════════════════════════════════════════════
   DASHBOARD DESIGN TOKENS — per color mode
   Chart/status hues in both modes validated with the dataviz palette
   checker against each mode's card surface:
     light series on #ffffff → ALL PASS (worst adjacent CVD ΔE 16.9)
     dark  series on #111a2c → ALL PASS (worst adjacent CVD ΔE 13.6)
   NOTE: brighter dark candidates (#22c55e/#14b8a6/#a78bfa/#f59e0b)
   FAIL the dark lightness band — don't "improve" these values.
══════════════════════════════════════════════════════════════════ */

export const F = {
  sans: "'Inter', 'Helvetica Neue', sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
};

export type DashTokens = {
  pageBg: string; pageBgTop: string;
  headerBg: string; headerBg2: string; headerBorder: string;
  card: string; cardBorder: string; cardHover: string; tableTotalBg: string;
  ink: string; sub: string; muted: string;
  onDark: string; onDarkMuted: string;
  teal: string; tealDk: string; tealLt: string;
  green: string; greenLt: string;
  red: string; redLt: string; redMid: string;
  amber: string; amberLt: string;
  blue: string; blueLt: string;
  purple: string; purpleLt: string;
  orange: string; orangeLt: string;
  gray: string;
  divider: string;
};

export const TOKENS: Record<Mode, DashTokens> = {
  light: {
    pageBg: "#f1f5f9",
    pageBgTop: "#eef2f7",
    headerBg: "#0f1f35",
    headerBg2: "#142a47",
    headerBorder: "rgba(255,255,255,0.06)",
    card: "#ffffff",
    cardBorder: "#e2e8f0",
    cardHover: "#f8fafc",
    tableTotalBg: "#f8faff",
    ink: "#0f1f35",
    sub: "#475569",
    muted: "#94a3b8",
    onDark: "rgba(255,255,255,0.90)",
    onDarkMuted: "rgba(255,255,255,0.45)",
    teal: "#0d9488",   tealDk: "#0f766e",  tealLt: "#ccfbf1",
    green: "#16a34a",  greenLt: "#dcfce7",
    red: "#dc2626",    redLt: "#fef2f2",   redMid: "#f87171",
    amber: "#d97706",  amberLt: "#fef3c7",
    blue: "#2563eb",   blueLt: "#dbeafe",
    purple: "#7c3aed", purpleLt: "#ede9fe",
    orange: "#ea580c", orangeLt: "#fff7ed",
    gray: "#94a3b8",
    divider: "#f1f5f9",
  },
  dark: {
    pageBg: "#0a1120",
    pageBgTop: "#0d1526",
    headerBg: "#182740",   // ChartTip/map tooltip bg — must sit above the dark card
    headerBg2: "#1e3050",
    headerBorder: "rgba(255,255,255,0.08)",
    card: "#111a2c",
    cardBorder: "rgba(255,255,255,0.08)",
    cardHover: "#16213a",
    tableTotalBg: "rgba(77,163,255,0.06)",
    ink: "#e8eef9",
    sub: "rgba(232,238,249,0.72)",
    muted: "rgba(232,238,249,0.45)",
    onDark: "rgba(255,255,255,0.92)",
    onDarkMuted: "rgba(255,255,255,0.50)",
    teal: "#0d9488",   tealDk: "#0f766e",  tealLt: "rgba(13,148,136,0.16)",
    green: "#16a34a",  greenLt: "rgba(22,163,74,0.16)",
    red: "#ef4444",    redLt: "rgba(239,68,68,0.14)",  redMid: "#b91c1c",
    amber: "#d97706",  amberLt: "rgba(217,119,6,0.16)",
    blue: "#3b82f6",   blueLt: "rgba(59,130,246,0.16)",
    purple: "#8b5cf6", purpleLt: "rgba(139,92,246,0.16)",
    orange: "#ea580c", orangeLt: "rgba(234,88,12,0.16)",
    gray: "#94a3b8",
    divider: "rgba(255,255,255,0.06)",
  },
};

export type DashShadows = { sm: string; md: string; lg: string; xl: string };

export const SHADOWS: Record<Mode, DashShadows> = {
  light: {
    sm: "0 1px 3px rgba(15,31,53,0.06), 0 1px 2px rgba(15,31,53,0.04)",
    md: "0 6px 20px rgba(15,31,53,0.09)",
    lg: "0 10px 36px rgba(15,31,53,0.13)",
    xl: "0 22px 64px rgba(15,31,53,0.16)",
  },
  dark: {
    sm: "0 1px 3px rgba(0,0,0,0.50), 0 1px 2px rgba(0,0,0,0.40)",
    md: "0 6px 20px rgba(0,0,0,0.45)",
    lg: "0 10px 36px rgba(0,0,0,0.55)",
    xl: "0 22px 64px rgba(0,0,0,0.60)",
  },
};

export const makeCard = (C: DashTokens, S: DashShadows) => (extra?: object) => ({
  background: C.card,
  border: `1px solid ${C.cardBorder}`,
  borderRadius: "16px",
  boxShadow: S.sm,
  transition: "box-shadow .25s ease, transform .25s ease, background-color .3s ease, border-color .3s ease",
  ...extra,
});

// Status colors stay 6-digit hex in both modes, so hex+alpha-suffix concat remains valid
export const riskClr = (C: DashTokens, s: string = "") =>
  s.toLowerCase().includes("high") ? C.red : s.toLowerCase().includes("mod") ? C.amber : C.green;
export const riskBgClr = (C: DashTokens, s: string = "") => riskClr(C, s) + "1a";

export function useDashTokens() {
  const { mode } = useColorMode();
  return useMemo(
    () => ({
      mode,
      C: TOKENS[mode],
      S: SHADOWS[mode],
      card: makeCard(TOKENS[mode], SHADOWS[mode]),
      glow: (hex: string) => (mode === "dark" ? `0 0 18px ${hex}66` : "none"),
    }),
    [mode]
  );
}
